import isNative from '@unifyapps/network/utils/native/isNative';
import _map from 'lodash/map';
import { useMemo } from 'react';
import { getInterfaceThemeRootElement } from './interfaceThemeRoot';

export { getInterfaceThemeRootElement, INTERFACE_THEME, UA_THEME_VARS } from './interfaceThemeRoot';

export const getCSSVariablesValue = ({
  variable,
  styles,
}: {
  variable: string;
  styles: CSSStyleDeclaration | null;
}) => {
  if (!styles) {
    // If the variable is a hex value, return it directly
    return variable.startsWith('#') ? variable : `var(${variable})`;
  }

  return styles.getPropertyValue(variable);
};

export const getInterfaceStyles = () => {
  /**
   * Get the interface node if it exists, otherwise get the platform node
   * Because `interface-theme` class is not present if the theme is not overridden
   */
  if (isNative()) {
    return { interfaceStyles: null };
  }

  // document is not defined during SSR, so document.querySelector will throw an error
  if (typeof document === 'undefined') {
    return { interfaceStyles: null };
  }

  const styleNode = getInterfaceThemeRootElement();

  const interfaceStyles = styleNode ? getComputedStyle(styleNode) : null;

  return { interfaceStyles };
};

export const useInterfaceStyles = () => useMemo(() => getInterfaceStyles(), []);

export const useInterfaceThemeColorToken = ({ color }: { color?: string }) => {
  const { interfaceStyles } = useInterfaceStyles();

  return useMemo(
    () => (color ? getCSSVariablesValue({ variable: color, styles: interfaceStyles }) : null),
    [color, interfaceStyles],
  );
};

export const useInterfaceThemeColorsToken = ({ colors }: { colors?: string[] }) => {
  const { interfaceStyles } = useInterfaceStyles();

  return useMemo(
    () =>
      _map(colors, (color) => ({
        // If the color is a hex value, no need to get the CSS variable value
        colorToken: getCSSVariablesValue({
          variable: color,
          styles: color.startsWith('#') ? null : interfaceStyles,
        }),
        color,
      })),
    [colors, interfaceStyles],
  );
};

export const useInterfaceThemeStyleVariableValues = ({ variables }: { variables: string[] }) => {
  const { interfaceStyles } = useInterfaceStyles();

  return useMemo(
    () => variables.map((variable) => interfaceStyles?.getPropertyValue(variable)),
    [variables, interfaceStyles],
  );
};
